// #2827 (scribe-resilience workstream, docs/score/scribe-resilience.md gap
// G12 part B) — pure row-building logic for the station monitor, unit
// tested independently of React (same split as util/scribePresence.js).
//
// Two data sources, joined here, never fetched or computed anywhere else:
//   - snapshotList: score-api GET /show/:showId/station-snapshot (#2827's
//     read side of #2929's intake route) — one row per station that has
//     EVER uploaded a snapshot, keyed by (deviceId, scribeSub), with counts
//     computed from that station's own snapshot (never guessed, AC2).
//   - presenceList: score-api GET /presence (#2363) — one row per LIVE
//     scribe-fe instance, keyed by instanceId alone.
//
// AC4's "never uploaded" distinction only exists because of this join: a
// presence row (the tent knows a scribe is signed in right now) whose
// instanceId has no matching snapshot at all is flagged `neverUploaded`,
// visibly distinct from a row that HAS a snapshot showing zero queued items
// (a real, healthy "nothing queued" station). A row with no presence match
// still renders fine from its snapshot alone (station went fully offline,
// presence's TTL already dropped it — the snapshot is the belt to that
// braces, same "kept forever vs. live-only" relationship the rest of G11/G12
// documents).
//
// Join key assumption (documented on station-snapshot.js's own schema
// comment): deviceId (snapshot) and instanceId (presence) are "the same
// shape" and are treated here as the SAME client-generated identifier for
// one physical station.
//
// #2827 judge fix r1 (FE item 2): the "never uploaded" decision is keyed on
// the SAME (deviceId, scribeSub) pair as the snapshot rows, not on deviceId
// alone. A tablet can change hands mid-show — snapshot history under an
// earlier scribe (A), live presence now under a different one (B) — and
// keying on deviceId alone made B's live row vanish entirely, because A's
// snapshot already "claimed" that deviceId. B is a different (deviceId,
// scribeSub) pair and must still render, just with no snapshot of its own
// yet. `deviceSeenElsewhere` on that row records that the physical device
// DOES have snapshot history (just under a different scribe), so the UI can
// say "no snapshot under this scribe" rather than implying the station
// itself has never been heard from.

export function stationKey(deviceId, scribeSub) {
  return `${deviceId ?? ""}::${scribeSub ?? ""}`;
}

function countsFromSnapshot(snap) {
  return {
    shipped: snap.shipped,
    queued: snap.queued,
    needsAttention: snap.needsAttention,
  };
}

// The single value that "predicts a bad afternoon" (the card's own framing):
// the EARLIEST oldestUnshippedAt across every station, i.e. how long the
// single oldest unshipped sheet in the whole show has been sitting. ISO 8601
// timestamps compare correctly as plain strings, so a lexicographic min is a
// correct chronological min.
function earliestTimestamp(timestamps) {
  const real = timestamps.filter(Boolean);
  if (!real.length) return null;
  return real.reduce((oldest, t) => (t < oldest ? t : oldest));
}

export function buildStationMonitorRows({ presenceList = [], snapshotList = [] } = {}) {
  const deviceIdsWithSnapshot = new Set(snapshotList.map((s) => s.deviceId));
  const rowsByKey = new Map();

  for (const snap of snapshotList) {
    const key = stationKey(snap.deviceId, snap.scribeSub);
    const presenceMatch = presenceList.find(
      (p) => p.instanceId === snap.deviceId && p.scribeSub === snap.scribeSub
    );
    rowsByKey.set(key, {
      key,
      deviceId: snap.deviceId,
      scribeSub: snap.scribeSub,
      scribeName: presenceMatch?.scribeName ?? null,
      ring: presenceMatch?.ring ?? null,
      classId: presenceMatch?.classId ?? null,
      lastSeenAt: presenceMatch?.lastSeenAt ?? snap.exportedAt ?? null,
      hasSnapshot: true,
      neverUploaded: false,
      snapshotAt: snap.snapshotAt,
      counts: countsFromSnapshot(snap),
      oldestUnshippedAt: snap.oldestUnshippedAt,
      // #2828 (G12 part B) — score-api's listStationSnapshots now joins each
      // station's reconcile/review state (reconciledAt/reconciledBy,
      // disagreementCount, reviewed/reviewedBy/reviewedAt) onto the raw
      // snapshot row (see station-snapshot.js handler, judge-early-1 fix 5).
      // null here means "never reconciled" — distinct from a reconciled row
      // with zero disagreements, which still carries the reconciledAt stamp.
      reconcile: snap.reconcile ?? null,
    });
  }

  // Presence-only rows: a live (deviceId, scribeSub) pair the tent knows
  // about that has NEVER uploaded a snapshot UNDER THAT PAIR. This is the
  // ONLY source of `neverUploaded: true` rows — a station with no presence
  // AND no snapshot is invisible to both sources and cannot appear here,
  // which is correct: nothing observed it. Keyed the same way the snapshot
  // rows above are (stationKey(deviceId, scribeSub)) so a device that
  // changed hands mid-show still surfaces the NEW scribe's live row (#2827
  // judge fix r1, FE item 2) instead of being swallowed by the old scribe's
  // snapshot under the same deviceId.
  for (const p of presenceList) {
    if (!p.instanceId) continue;
    const key = stationKey(p.instanceId, p.scribeSub);
    if (rowsByKey.has(key)) continue;
    rowsByKey.set(key, {
      key,
      deviceId: p.instanceId,
      scribeSub: p.scribeSub ?? null,
      scribeName: p.scribeName ?? null,
      ring: p.ring ?? null,
      classId: p.classId ?? null,
      lastSeenAt: p.lastSeenAt ?? null,
      hasSnapshot: false,
      neverUploaded: true,
      deviceSeenElsewhere: deviceIdsWithSnapshot.has(p.instanceId),
      snapshotAt: null,
      counts: null,
      oldestUnshippedAt: null,
      reconcile: null,
    });
  }

  return [...rowsByKey.values()];
}

// Show-wide worst-case numbers for the dashboard strip (AC3) — deliberately
// built from the SAME rows the monitor page renders (buildStationMonitorRows
// above), so the strip and the page can never disagree about what counts as
// a station or a snapshot: one aggregation, two views.
//
// #2827 judge fix r1 (FE item 1): `queuedTotal` is exposed here so the strip
// can refuse to say "clean" while anything is still queued, not just while
// something needs attention — needsAttentionTotal alone under-reports a show
// with a pile of merely-queued (not yet flagged) sheets.
export function summarizeStationMonitorRows(rows) {
  const neverUploadedCount = rows.filter((r) => r.neverUploaded).length;
  const needsAttentionTotal = rows.reduce((sum, r) => sum + (r.counts?.needsAttention ?? 0), 0);
  const queuedTotal = rows.reduce((sum, r) => sum + (r.counts?.queued ?? 0), 0);
  const oldestUnshippedAt = earliestTimestamp(rows.map((r) => r.oldestUnshippedAt));
  return { neverUploadedCount, needsAttentionTotal, queuedTotal, oldestUnshippedAt };
}

// Groups rows by scribe (AC1: "grouped by scribe"), scribeless rows (a
// device/presence pair whose scribeSub somehow came back empty) collected
// under a single null-key group last so they're never silently dropped.
export function groupStationRowsByScribe(rows) {
  const groups = new Map();
  for (const row of rows) {
    const scribeKey = row.scribeSub || "";
    if (!groups.has(scribeKey)) {
      groups.set(scribeKey, { scribeSub: row.scribeSub, scribeName: row.scribeName, rows: [] });
    }
    const group = groups.get(scribeKey);
    // Prefer a non-null scribeName if a later row in the same group has one.
    if (!group.scribeName && row.scribeName) group.scribeName = row.scribeName;
    group.rows.push(row);
  }
  return [...groups.values()].sort((a, b) => {
    if (!a.scribeSub) return 1;
    if (!b.scribeSub) return -1;
    return (a.scribeName || a.scribeSub).localeCompare(b.scribeName || b.scribeSub);
  });
}
